import { PGTimeStampedRecord } from '@/lib/api/config';
import { TypeAddOnProduct, TypeProduct } from '@/lib/api/productCatalog/types';

/**
 * Status enums and types
 */
export enum SUBSCRIPTION_PRODUCT_ANCHOR {
  LOVEVERY_SUBSCRIPTION = 'subscription-1',
}
export type TypeSubscriptionProductAnchor =
  SUBSCRIPTION_PRODUCT_ANCHOR.LOVEVERY_SUBSCRIPTION;

export enum ADDON_KIND {
  BOOKS = 'books_addon',
}
export type TypeAddonKind = ADDON_KIND.BOOKS;

export enum ADDON_STATUS {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  CANCELLED = 'cancelled',
}
export type TypeAddonStatus =
  | ADDON_STATUS.ACTIVE
  | ADDON_STATUS.INACTIVE
  | ADDON_STATUS.CANCELLED;

export enum SCHEDULED_TYPE {
  SCHEDULED = 'scheduled',
  INITIAL_ITEM = 'initial_item',
  PREVIOUS_PLAY = 'previous_play',
}
export type TypeScheduledType =
  | SCHEDULED_TYPE.SCHEDULED
  | SCHEDULED_TYPE.INITIAL_ITEM
  | SCHEDULED_TYPE.PREVIOUS_PLAY;

export enum PROCESS_STATUS {
  AWAITING_PROCESS = 'awaiting_process',
  SKIP_REQUESTED = 'skip_requested',
  SKIPPED = 'skipped',
  BILLING = 'billing',
  SHIPPING = 'shipping',
  COMPLETED = 'completed',
  ERROR = 'error',
  NOT_APPLICABLE = 'not_applicable',
  CANCELLED = 'cancelled',
  RETURNED = 'returned',
  SHIPMENT_PREPARATION = 'shipment_preparation',
}
export type TypeProcessStatus =
  | PROCESS_STATUS.AWAITING_PROCESS
  | PROCESS_STATUS.SKIP_REQUESTED
  | PROCESS_STATUS.SKIPPED
  | PROCESS_STATUS.BILLING
  | PROCESS_STATUS.SHIPPING
  | PROCESS_STATUS.COMPLETED
  | PROCESS_STATUS.ERROR
  | PROCESS_STATUS.NOT_APPLICABLE
  | PROCESS_STATUS.CANCELLED
  | PROCESS_STATUS.RETURNED
  | PROCESS_STATUS.SHIPMENT_PREPARATION;

export enum DELIVERY_STATUS {
  NOT_SHIPPED = 'not_shipped',
  WAREHOUSE_NOTIFIED = 'warehouse_notified',
  SHIPPED = 'shipped',
  ERROR = 'error',
  RETURN = 'return',
  SCHEDULED = 'scheduled',
}
export type TypeDeliveryStatus =
  | DELIVERY_STATUS.NOT_SHIPPED
  | DELIVERY_STATUS.WAREHOUSE_NOTIFIED
  | DELIVERY_STATUS.SHIPPED
  | DELIVERY_STATUS.ERROR
  | DELIVERY_STATUS.RETURN
  | DELIVERY_STATUS.SCHEDULED;

export enum BILLING_STATUS {
  AWAITING_PAYMENT = 'awaiting_payment',
  PAID = 'paid',
  PREPAY = 'prepay',
  ERROR = 'error',
  REFUND = 'refund',
  PENDING_CLEARANCE = 'pending_clearance',
}
export type TypeBillingStatus =
  | BILLING_STATUS.AWAITING_PAYMENT
  | BILLING_STATUS.PAID
  | BILLING_STATUS.PREPAY
  | BILLING_STATUS.ERROR
  | BILLING_STATUS.REFUND
  | BILLING_STATUS.PENDING_CLEARANCE;

export enum DELIGHT_ITEM_STATUS {
  FULFILLED = 'fulfilled',
  UNFULFILLED = 'unfulfilled',
  FULFILLMENT_IN_PROGRESS = 'fulfillment_in_progress',
}
export type TypeDelightItemStatus =
  | DELIGHT_ITEM_STATUS.FULFILLED
  | DELIGHT_ITEM_STATUS.UNFULFILLED
  | DELIGHT_ITEM_STATUS.FULFILLMENT_IN_PROGRESS;

export enum DOLL_SKIN_TONE_OPTIONS {
  LIGHT_SKIN_TONE = 'light_skin_tone',
  TAN_SKIN_TONE = 'tan_skin_tone',
  DARK_SKIN_TONE = 'dark_skin_tone',
}
export type TypeDollSkinToneOptions =
  | DOLL_SKIN_TONE_OPTIONS.LIGHT_SKIN_TONE
  | DOLL_SKIN_TONE_OPTIONS.TAN_SKIN_TONE
  | DOLL_SKIN_TONE_OPTIONS.DARK_SKIN_TONE;

export enum SECTION_TYPE {
  SCHEDULED = 'scheduled',
  INITIAL = 'initial',
  PREVIEW = 'preview',
  TRANSITION = 'transition',
  IMPORTED = 'imported',
}
export type TypeSectionType =
  | SECTION_TYPE.SCHEDULED
  | SECTION_TYPE.INITIAL
  | SECTION_TYPE.PREVIEW
  | SECTION_TYPE.TRANSITION
  | SECTION_TYPE.IMPORTED;

export enum SUBSCRIPTION_STATUS {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  CANCELLED = 'cancelled',
  CREATED = 'created',
  FINISHED = 'finished',
  ERROR = 'error',
}
export type TypeSubscriptionStatus =
  | SUBSCRIPTION_STATUS.ACTIVE
  | SUBSCRIPTION_STATUS.INACTIVE
  | SUBSCRIPTION_STATUS.CANCELLED
  | SUBSCRIPTION_STATUS.CREATED
  | SUBSCRIPTION_STATUS.FINISHED
  | SUBSCRIPTION_STATUS.ERROR;

export enum SECTION_STATUS {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  FUTURE = 'future',
  COMPLETE = 'complete',
}
export type TypeSectionStatus =
  | SECTION_STATUS.ACTIVE
  | SECTION_STATUS.CANCELLED
  | SECTION_STATUS.FUTURE
  | SECTION_STATUS.COMPLETE;

export enum SUBSCRIPTION_LANGUAGE_OPTIONS {
  EN = 'en',
  EN_GB = 'en_GB',
  DE = 'de',
  NL = 'nl',
  FR = 'fr',
  ES = 'es',
}
export type TypeLanguageOptions =
  | SUBSCRIPTION_LANGUAGE_OPTIONS.EN
  | SUBSCRIPTION_LANGUAGE_OPTIONS.EN_GB
  | SUBSCRIPTION_LANGUAGE_OPTIONS.DE
  | SUBSCRIPTION_LANGUAGE_OPTIONS.NL
  | SUBSCRIPTION_LANGUAGE_OPTIONS.FR
  | SUBSCRIPTION_LANGUAGE_OPTIONS.ES;

/**
 * Record Types
 */
export type TypeSelectedOptions = {
  language?: TypeLanguageOptions;
  doll_skin_tone?: TypeDollSkinToneOptions;
};

export interface TypeAddonPreferenceRecord extends PGTimeStampedRecord {
  addon_kind: TypeAddonKind;
  addon_status: TypeAddonStatus;
  cancellation_reason?: string;
}

export interface TypeDelightItemRecord extends PGTimeStampedRecord {
  status: TypeDelightItemStatus;
}

export interface TypeScheduledAddOnRecord {
  uuid: string;
  product_anchor: string;
  product_uuid: string;
  variant_uuid: string;
  invoice_uuid?: string;
  billing_status: TypeBillingStatus;
  billing_date?: string; // timestamp
  billing_error_message?: string;
  addon_kind: TypeAddonKind;
  addon_status: TypeAddonStatus;
}

export interface TypeSubscriptionPreferenceRecord {
  subscription_product_anchor: TypeSubscriptionProductAnchor;
  subscription_variant_uuid: string;
  product_choice_uuids: string[]; // unused
  selected_options: TypeSelectedOptions;
  created_at: string; //timestamp
  updated_at: string; //timestamp
}

export interface TypeScheduledItemRecord extends PGTimeStampedRecord {
  scheduled_type: TypeScheduledType;
  process_status: TypeProcessStatus;
  billing_status: TypeBillingStatus;
  delivery_status: TypeDeliveryStatus;
  product_uuid: string;
  scheduled_date: string; // date
  original_scheduled_date: string; // date
  playgenius_order_id?: string;
  subscription_uuid: string;
  subscription_variant_uuid: string;
  variant_uuid: string;
  user_uuid: string;
  billing_date?: string; // timestamp
  shipping_date?: string; // timestamp
  processed_date?: string; // timestamp
  order_uuid?: string | null; // shopify order id
  invoice_uuid?: string; // fulfillment invoice
  original_invoice_uuid?: string | null; // prepayment invoice
  retry_date?: string | null; // timestamp
  retry_count?: number;
  billing_error_message?: string | null;
  shipping_error_message?: string | null;
  skip_reason?: string | null;
  notes?: string | null;
  order_number?: string | null; // shopify order display id
  scheduled_addons?: TypeScheduledAddOnRecord[];
  delight_item_addons?: TypeDelightItemRecord[];
  product_anchor?: string;
  skippable?: boolean;
}

export interface TypeProductDecoratedScheduledItem
  extends TypeScheduledItemRecord {
  product?: TypeProduct;
  bookBundleProduct?: TypeAddOnProduct;
}

export interface TypeScheduleSectionRecord extends PGTimeStampedRecord {
  section_type: TypeSectionType;
  section_status: TypeSectionStatus;
  section_source: string;
  activation_code?: string;
  subscription_preference: TypeSubscriptionPreferenceRecord;
  scheduled_items: TypeScheduledItemRecord[];
}

export interface TypeSubscriptionRecord extends PGTimeStampedRecord {
  recipient_uuid: string;
  // for the foreseeable future, there will only ever be one purchaser for a subscription
  purchaser_uuids: string[];
  child_uuid: string;
  bundle_discount_uuid?: string;
  delivery_address_uuid: string;
  billing_address_uuid: string;
  subscription_status: TypeSubscriptionStatus;
  subscription_type: 'play_kits' | 'erc';
  subscription_source: string;
  expiration_date?: string; // unused
  cancellation_reason?: string;
  cancel_reason_comment?: string;
  notes?: string; // json object
  error_message?: string;
  error_type?: 'none' | 'billing_error';
  billing_error_count?: number;
  auto_extensions_enabled?: boolean; // unused
  partial_subscription?: boolean;
  is_babylist?: boolean;
  schedule_sections: TypeScheduleSectionRecord[];
  addon_preferences?: TypeAddonPreferenceRecord[];
  next_kit_name?: string;
  next_billing_date?: string;
  next_kit_anchor?: string;
}

export enum SUBSCRIPTION_PAYMENT_PLAN {
  PAYGO = 'paygo',
  PUF = 'pay-up-front',
  REPUF = 'recurring-pay-up-front',
}

export type TypeSubscriptionPaymentPlan =
  | SUBSCRIPTION_PAYMENT_PLAN.PAYGO
  | SUBSCRIPTION_PAYMENT_PLAN.PUF
  | SUBSCRIPTION_PAYMENT_PLAN.REPUF;

export type TypeGiftOrder = {
  id: number;
  uuid: string;
  created_at: string;
  updated_at: string;
  activated_at: string;
  gift_status: string;
  activation_code: string;
  activation_source: string;
  source: string;
  message: string;
  recipient_uuid: string;
  purchaser_uuid: string;
  recipient_first_name: string;
  recipient_last_name: string;
  recipient_email: string;
  email_notification_date: string;
  subscription_uuid: string;
  billing_address_uuid: string;
  child_uuid: string;
  prepayment_invoice_uuid: string;
  schedule_count: number;
  recipient_notified_at: string;
};

export type TypeSubscriptionCountData = Partial<
  Record<TypeSubscriptionStatus, number>
>;

// ##### Sub Engine #####
export interface TypeStartingInstallment {
  id: string;
  sku: string;
}

export interface TypeStartingInstallmentsResponse {
  starting_installments: TypeStartingInstallment[];
}

export interface TypeSubscriptionPlanInstallmentsResponse {
  data: TypeStartingInstallmentsResponse | null;
  ok: boolean;
}

type SubEngineSubscriptionStatus =
  | SubEngineSubscriptionStatusEnum.ACTIVE
  | SubEngineSubscriptionStatusEnum.CANCELLED
  | SubEngineSubscriptionStatusEnum.PAUSED
  | SubEngineSubscriptionStatusEnum.PENDING
  | SubEngineSubscriptionStatusEnum.SUSPENDED;

export enum SubEngineSubscriptionStatusEnum {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  PAUSED = 'paused',
  PENDING = 'pending',
  SUSPENDED = 'suspended',
}

export enum SubEngineInstallmentStatusEnum {
  SKIPPED = 'skipped',
  ORDERED = 'ordered',
  COMPLETE = 'complete',
  PROCESSING = 'processing',
  SCHEDULED = 'scheduled',
  CANCELLED = 'cancelled',
  PAUSED = 'paused',
  BILLING_ERROR = 'billing_error',
  SUSPENDED = 'suspended',
}

export interface TypeSubEngineInstallment {
  id: string;
  subscription_id: string;
  sku: string;
  product_anchor: string;
  scheduled_date: string;
  status: SubEngineInstallmentStatusEnum;
  order_id: string | null;
  original_scheduled_date: string;
  skippable: boolean;
  skipped_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TypeSubEngineSubscription {
  id: string;
  user_uuid: string;
  status: SubEngineSubscriptionStatus;
  activated_at: string | null;
  cancelled_at: string | null;
  shipping_address_uuid: string;
  external_payment_method_uuid: string;
  created_at: string;
  updated_at: string;
  installments: TypeSubEngineInstallment[];
  pausable: boolean;
}

export type TypePauseRelease = {
  id: string;
  subscription_id: string;
  created_at: string;
};

export interface TypePauseReleaseResponseData {
  pause_release: TypePauseRelease;
}

export interface TypeSubEngineSubscriptionsResponse {
  subscriptions: TypeSubEngineSubscription[];
}

export interface TypeSubEngineSubscriptionResponse {
  subscription: TypeSubEngineSubscription;
}

export type TypeSubEngineSubscriptionsResponseSuccess = {
  ok: boolean;
  data: TypeSubEngineSubscriptionsResponse;
};

export interface TypeSubEngineSubscriptionResponse {
  subscription: TypeSubEngineSubscription;
}

export interface TypeSubEngineSubscriptionInstallmentsResponse {
  installments: TypeSubEngineInstallment[];
}

export interface TypeSubEngineInstallmentResponse {
  installment: TypeSubEngineInstallment;
}
